import React from 'react';
import { api } from '../api.js';
import { Empty, Lbl, Loading, Modal, PH, Panel, Token } from './components.jsx';
import { Button, Checkbox, DataTable, Field, Pill } from './ds/fgcp-ds.jsx';

// board-admin.jsx — 게시판 관리(그룹웨어 관리자, 계획 문서 5.2 2-4a): 이름·분류·설명·정렬·사용, 읽기·쓰기·관리 규칙(전사·부서·역할).
// 권한·값 규칙은 서버가 판단한다(탭은 GET /boards의 actions.manage일 때만 보이고, 관리 API는 400·403·409로 이유를 돌려준다).

const { useState: uS, useEffect: uE, useCallback: uCb } = React;
const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, boxSizing: 'border-box' };
export const CATEGORY = { notice: '공지', general: '일반', docs: '문서함' };
const CATEGORY_NOTE = { notice: '새 글을 읽을 수 있는 사람에게 알리고 홈 공지에 보입니다', general: '보통 게시판', docs: '문서 분류로 나누고 첨부를 새 판으로 올립니다(이전 판 보존)' };
const ACCESS = [['read', '읽기'], ['write', '쓰기'], ['manage', '관리']];

/** 주체 이름표 — 전사·부서 이름·역할 이름 */
const principalLabel = (p) => (p.type === 'all' ? '전사' : p.type === 'department' ? `${p.name ?? '부서'}(하위 포함)` : (p.name || p.code));
const sameP = (a, b) => a.type === b.type && (a.type === 'all' || (a.type === 'department' ? a.id === b.id : a.code === b.code));

/** 부서를 트리 순서로(들여쓰기 깊이와 함께) — 고르는 목록이 조직도처럼 보이게 */
function deptTree(depts) {
  const kids = new Map();
  for (const d of depts) kids.set(d.parent_id ?? null, [...(kids.get(d.parent_id ?? null) ?? []), d]);
  const out = [];
  const walk = (pid, depth) => { for (const d of kids.get(pid) ?? []) { out.push({ ...d, depth }); walk(d.id, depth + 1); } };
  walk(null, 0);
  // 상위가 목록에 없는 부서(고아)도 빠지지 않게
  for (const d of depts) if (!out.some(x => x.id === d.id)) out.push({ ...d, depth: 0 });
  return out;
}

/** 한 단계(읽기·쓰기·관리)의 주체 칩과 더하기 */
function RuleRow({ access, label, list, onChange, departments, roles }) {
  const [dept, setDept] = uS('');
  const [role, setRole] = uS('');
  const add = (p) => { if (!list.some(x => sameP(x, p))) onChange([...list, p]); };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '9px 11px', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)' }} data-rule={access}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Lbl style={{ width: 34 }}>{label}</Lbl>
        {list.length ? list.map((p, i) => (
          <Token key={i} tone="accent" data-principal={p.type === 'all' ? 'all' : p.type === 'department' ? `department:${p.id}` : `role:${p.code}`}
            onRemove={() => onChange(list.filter((_, j) => j !== i))} removeLabel={`${principalLabel(p)} 빼기`}>{principalLabel(p)}</Token>)) : <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{access === 'manage' ? '없음 — 그룹웨어 관리자만' : '없음'}</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {access !== 'manage' && <Button type="button" size="sm" variant="ghost" onClick={() => add({ type: 'all' })} data-add-all={access}>+ 전사</Button>}
        <select style={box} value={dept} aria-label={`${label} 부서 더하기`} onChange={e => {
          const d = departments.find(x => x.id === e.target.value);
          if (d) add({ type: 'department', id: d.id, name: d.name });
          setDept('');
        }}>
          <option value="">+ 부서</option>
          {departments.map(d => <option key={d.id} value={d.id}>{'\u00a0\u00a0'.repeat(d.depth)}{d.name}</option>)}
        </select>
        <select style={box} value={role} aria-label={`${label} 역할 더하기`} onChange={e => {
          const r = roles.find(x => x.code === e.target.value);
          if (r) add({ type: 'role', code: r.code, name: r.name });
          setRole('');
        }}>
          <option value="">+ 역할</option>
          {roles.map(r => <option key={r.code} value={r.code}>{r.name ? `${r.name} (${r.code})` : r.code}</option>)}
        </select>
      </div>
    </div>
  );
}

/** 게시판 만들기·고치기 — 규칙은 보낸 것이 새 전체 */
function BoardEditor({ board, departments, roles, onClose, onSaved, onToast }) {
  const b = board;
  const [name, setName] = uS(b?.name ?? '');
  const [category, setCategory] = uS(b?.category ?? 'general');
  const [description, setDescription] = uS(b?.description ?? '');
  const [sortOrder, setSortOrder] = uS(String(b?.sort_order ?? 0));
  const [active, setActive] = uS(b?.active ?? true);
  // 새 게시판의 첫 규칙은 '읽기 전사' — 실제 범위는 관리자가 고른다
  const [rules, setRules] = uS(b?.rules ?? { read: [{ type: 'all' }], write: [], manage: [] });
  const [cats, setCats] = uS((b?.categories ?? []).map(c => ({ id: c.id, name: c.name, posts: c.post_count })));   // 문서함 분류(2-4c)
  const [newCat, setNewCat] = uS('');
  const [busy, setBusy] = uS(false);
  const save = () => {
    setBusy(true);
    const strip = list => list.map(p => (p.type === 'all' ? { type: 'all' } : p.type === 'department' ? { type: 'department', id: p.id } : { type: 'role', code: p.code }));
    const body = { name, category, description: description.trim() || null, sortOrder: Number(sortOrder), active,
      rules: { read: strip(rules.read), write: strip(rules.write), manage: strip(rules.manage) },
      // 문서 분류는 문서함만 — 다른 분류로 바꾸면 비운다(글이 있는 분류는 서버가 409로 막는다)
      categories: category === 'docs' ? cats.map(c => ({ ...(c.id ? { id: c.id } : {}), name: c.name.trim() })) : [] };
    (b ? api.put('/api/v1/boards/' + b.id, body) : api.post('/api/v1/boards', body))
      .then(() => { onToast(b ? '게시판을 고쳤습니다' : '게시판을 만들었습니다'); onSaved(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={600} title={b ? `게시판 고치기 — ${b.name}` : '새 게시판'} icon="megaphone" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        <Button variant="primary" size="sm" disabled={busy || !name.trim()} onClick={save}>저장</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-board-editor>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="이름" value={name} onChange={e => setName(e.target.value)} maxLength={50} style={{ flex: '2 1 220px' }} />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>분류</Lbl>
            <select style={box} value={category} onChange={e => setCategory(e.target.value)} aria-label="분류">
              {Object.entries(CATEGORY).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>정렬</Lbl>
            <input type="number" min="0" max="9999" style={{ ...box, width: 90 }} value={sortOrder} onChange={e => setSortOrder(e.target.value)} aria-label="정렬 순서" /></label>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{CATEGORY[category]}: {CATEGORY_NOTE[category]}</div>
        <Field label="설명 (선택)" value={description} onChange={e => setDescription(e.target.value)} maxLength={300} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ACCESS.map(([a, l]) => <RuleRow key={a} access={a} label={l} list={rules[a]} departments={departments} roles={roles}
            onChange={list => setRules(r => ({ ...r, [a]: list }))} />)}
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            부서 규칙은 하위 부서를 포함합니다. 쓰기는 읽기를, 관리는 쓰기·읽기를 포함합니다. 관리(고정·중요, 남의 글 고치기·지우기)는 부서·역할만 —
            그룹웨어 관리자는 언제나 모든 게시판을 관리합니다.
          </div>
        </div>
        {category === 'docs' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-board-categories>
            <Lbl>문서 분류 (위에서부터 순서 — 글이 있는 분류는 뺄 수 없습니다)</Lbl>
            {cats.map((c, i) => (
              <div key={c.id ?? 'new' + i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={{ ...box, flex: 1 }} value={c.name} maxLength={30} aria-label={`분류 ${i + 1}`}
                  onChange={e => setCats(list => list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                {c.posts > 0 && <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>글 {c.posts}</span>}
                <button type="button" className="link" disabled={i === 0} onClick={() => setCats(l => { const n = [...l]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}>위로</button>
                <button type="button" className="link" onClick={() => setCats(l => l.filter((_, j) => j !== i))} aria-label={`분류 ${c.name} 빼기`}>빼기</button>
              </div>))}
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={{ ...box, flex: 1 }} value={newCat} maxLength={30} placeholder="새 분류 이름" aria-label="새 분류 이름" onChange={e => setNewCat(e.target.value)} />
              <Button variant="secondary" size="sm" disabled={!newCat.trim() || cats.some(c => c.name.trim() === newCat.trim())}
                onClick={() => { setCats(l => [...l, { name: newCat.trim() }]); setNewCat(''); }}>분류 더하기</Button>
            </div>
          </div>
        )}
        <Checkbox label="사용" description="끄면 목록·검색·홈에서 빠지고 새 글을 받지 않습니다 — 이미 있는 글은 링크로 읽을 수 있습니다" checked={active} onChange={setActive} />
      </div>
    </Modal>
  );
}

function BoardAdmin({ onToast, onChanged }) {
  const [data, setData] = uS(null);
  const [editing, setEditing] = uS(null);   // 고칠 게시판 또는 'new'
  const reload = uCb(() => api.get('/api/v1/boards/manage')
    .then(r => setData({ ...r, departments: deptTree(r.departments) }))
    .catch(e => { setData({ boards: [], departments: [], roles: [] }); onToast(e.message); }), [onToast]);
  uE(() => { void reload(); }, [reload]);
  const summary = rules => ACCESS.map(([a, l]) => `${l} ${rules[a].length ? rules[a].map(principalLabel).join(', ') : (a === 'manage' ? '관리자만' : '없음')}`).join(' · ');
  return (
    <Panel scroll>
      <PH title="게시판" sub={data ? `${data.boards.length}개 · 규칙은 저장 즉시 목록·검색·첨부에 쓰입니다` : ''}
        right={<Button variant="primary" size="sm" onClick={() => setEditing('new')}>새 게시판</Button>} />
      {!data ? <Loading title="불러오는 중" /> : (
        <div data-board-admin>
          <DataTable columns={[
            { key: 'name', header: '게시판', strong: true, render: b => <button className="link" onClick={() => setEditing(b)} data-board-row={b.id}>{b.name}</button> },
            { key: 'category', header: '분류', width: 70, render: b => CATEGORY[b.category] },
            { key: 'rules', header: '규칙', render: b => <span style={{ fontSize: 11.5 }} data-board-rules={b.id}>{summary(b.rules)}</span> },
            { key: 'sort_order', header: '정렬', mono: true, align: 'right', width: 52 },
            { key: 'post_count', header: '글', mono: true, align: 'right', width: 52 },
            { key: 'active', header: '상태', width: 64, render: b => (b.active ? <Pill tone="ok" dot>사용</Pill> : <Pill tone="neutral" dot={false}>중지</Pill>) },
          ]} rows={data.boards} />
        </div>
      )}
      {editing && data && <BoardEditor board={editing === 'new' ? null : editing} departments={data.departments} roles={data.roles} onToast={onToast}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void reload(); onChanged(); }} />}
    </Panel>
  );
}

export { BoardAdmin };
